/**
 * Formateo para pantalla. Logica pura: entra un dato de la API, sale una
 * cadena. No toca el DOM ni la red, y por eso es lo que esta probado.
 *
 * ---------------------------------------------------------------------------
 * POR QUE NO SE USA Intl.NumberFormat PARA LOS MONTOS
 *
 * Porque recibe un `number`. La API manda los montos como CADENA justamente
 * para que no pasen por el punto flotante de JavaScript [CLAUDE.md, Dinero], y
 * `Intl.NumberFormat().format(Number('5320.95'))` deshace eso en el ultimo
 * metro: el valor que se pinta ya no es el que se congelo. Aqui la cadena entra
 * a `Decimal`, se fija a dos decimales con `Decimal.toFixed` --que no es el
 * `toFixed` de Number-- y los separadores se ponen sobre el texto resultante.
 * En ningun punto hay un `number` con un monto dentro.
 *
 * Los montos llegan ya redondeados: `montoTexto` de pricing redondea una sola
 * vez al final del calculo de cada linea, y `opcion_linea.venta` es
 * numeric(14,4) con dos decimales significativos. Este modulo no vuelve a
 * redondear un calculo, solo recorta la cola de ceros para pintarla.
 * ---------------------------------------------------------------------------
 */

import Decimal from 'decimal.js';

export type Idioma = 'es' | 'en';

/** Lo que se pinta cuando un dato no viene. Nunca una celda en blanco: un
 *  hueco y un cero se ven igual y significan cosas opuestas [H59]. */
export const AUSENTE = '—';

interface Separadores {
  readonly miles: string;
  readonly decimal: string;
}

const SEPARADORES: Record<Idioma, Separadores> = {
  es: { miles: '.', decimal: ',' },
  en: { miles: ',', decimal: '.' },
};

/** Agrupa de tres en tres sobre el texto, no sobre el numero. */
function agrupar(entera: string, miles: string): string {
  return entera.replace(/\B(?=(\d{3})+(?!\d))/g, miles);
}

/**
 * Un monto en la notacion del idioma. `null`, `undefined` y cadena vacia dan
 * el guion: la ausencia se pinta, no se rellena con cero.
 */
export function formatearMonto(valor: string | null | undefined, idioma: Idioma): string {
  if (valor === null || valor === undefined || valor.trim() === '') return AUSENTE;

  const d = new Decimal(valor);
  const fijo = d.toFixed(2);
  const negativo = fijo.startsWith('-');
  const [entera, decimales] = (negativo ? fijo.slice(1) : fijo).split('.');
  const s = SEPARADORES[idioma];

  return `${negativo ? '-' : ''}${agrupar(entera, s.miles)}${s.decimal}${decimales}`;
}

/** El monto con su codigo de moneda delante. Codigo y no simbolo: 'USD' es un
 *  codigo estable y '$' es una adivinanza entre siete paises. */
export function formatearMoneda(
  valor: string | null | undefined,
  moneda: string | null,
  idioma: Idioma,
): string {
  const monto = formatearMonto(valor, idioma);
  if (monto === AUSENTE || moneda === null) return monto;
  return `${moneda} ${monto}`;
}

/** Si un monto es cero, comparado como monto. `'0'`, `'0.00'` y `'0.0000'` son
 *  el mismo numero y llegan de sitios distintos --`montoTexto` y
 *  `numeric(14,4)`--, asi que compararlos como cadenas fallaria en la mitad. */
export function esCero(valor: string | null | undefined): boolean {
  if (valor === null || valor === undefined || valor.trim() === '') return false;
  return new Decimal(valor).isZero();
}

/** Dos cotas iguales no son un rango. Comparar con Decimal y no con `===`
 *  sobre las cadenas: '400' y '400.0000' son el mismo monto. */
export function esRango(min: string | null, max: string | null): boolean {
  if (min === null || max === null) return false;
  return !new Decimal(min).eq(new Decimal(max));
}

/**
 * El total de una tarifa. Cuando las dos cotas difieren se pintan las dos:
 * [H48] un cargo que vino en rango deja un total con dos cotas, y colapsarlo a
 * una sola afirma una precision que el proveedor no dio.
 */
export function formatearRango(
  min: string | null,
  max: string | null,
  moneda: string | null,
  idioma: Idioma,
): string {
  if (!esRango(min, max)) return formatearMoneda(min ?? max, moneda, idioma);
  return `${formatearMoneda(min, moneda, idioma)} – ${formatearMonto(max, idioma)}`;
}

/** Un porcentaje que llega como cadena ('5.75'). Mismo motivo que los montos:
 *  no pasa por Number. */
/**
 * Un porcentaje YA EN ESCALA de cien: `12.34` se pinta como `12,34 %`.
 *
 * Es lo que manda la API en `margen_pct`, que ya viene multiplicado desde el
 * motor. Para una FRACCION --0.08, que es como se guarda un markup en
 * `regla_markup.valor`-- esta el de abajo.
 */
export function formatearPorcentaje(
  valor: string | null | undefined,
  idioma: Idioma,
): string {
  if (valor === null || valor === undefined || valor.trim() === '') return AUSENTE;
  return `${formatearMonto(valor, idioma)} %`;
}

/**
 * Una FRACCION pintada como porcentaje: `0.08` se pinta como `8 %`.
 *
 * Existe aparte del de arriba a proposito, y no como un parametro: las dos
 * escalas conviven en la misma pantalla --`margen_pct` llega en cien,
 * `regla_markup.valor` llega en uno-- y un booleano `yaEnCien` acabaria puesto
 * al reves. Dos nombres distintos para dos cosas distintas.
 *
 * El x100 va con `Decimal` y no con `Number`: multiplicar por cien en punto
 * flotante convierte 0.07 en 7.000000000000001 [CLAUDE.md, Dinero]. Y se
 * recorta la cola de ceros --`8 %` y no `8,00 %`-- porque un markup se escribe
 * con los decimales que tiene: 5 %, 7,5 %, 12,25 %.
 */
export function formatearFraccionPorcentual(
  valor: string | null | undefined,
  idioma: Idioma,
): string {
  if (valor === null || valor === undefined || valor.trim() === '') return AUSENTE;

  const pct = new Decimal(valor).times(100);
  // `toFixed(2)` y despues quitar los ceros de la cola: `toString()` a secas
  // dejaria pasar 7.000000000000001 si el valor llegara ya sucio de otro sitio.
  const fijo = pct.toFixed(2).replace(/\.?0+$/, '');
  const negativo = fijo.startsWith('-');
  const [entera, decimales] = (negativo ? fijo.slice(1) : fijo).split('.');
  const s = SEPARADORES[idioma];
  const cuerpo =
    decimales === undefined
      ? agrupar(entera, s.miles)
      : `${agrupar(entera, s.miles)}${s.decimal}${decimales}`;

  return `${negativo ? '-' : ''}${cuerpo} %`;
}

/**
 * Una fecha ISO a la notacion del idioma, partiendo la cadena.
 *
 * Sin `new Date`, por el mismo motivo por el que la API pide las fechas con
 * `::text`: `new Date('2026-12-31')` es medianoche UTC, y al oeste de Greenwich
 * se pinta como el 30. Una vigencia que termina el 31 no puede mostrarse como
 * el 30 [Invariante 3].
 */
export function formatearFecha(iso: string | null | undefined, idioma: Idioma): string {
  if (iso === null || iso === undefined) return AUSENTE;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso.trim());
  if (m === null) return iso;
  const [, anio, mes, dia] = m;
  return idioma === 'es' ? `${dia}/${mes}/${anio}` : `${mes}/${dia}/${anio}`;
}

/** Tránsito en dias: un valor, un rango, o nada declarado. */
export function formatearTransito(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  if (min === null || max === null) return String(min ?? max);
  return min === max ? String(min) : `${min}–${max}`;
}

// ---------------------------------------------------------------------------
// Dias libres
// ---------------------------------------------------------------------------

export interface DiasLibres {
  readonly demora: number | null;
  readonly detencion: number | null;
  readonly minimo: number | null;
}

export type TipoDiaLibre = 'demora' | 'detencion' | 'minimo';

export interface DiaLibre {
  readonly tipo: TipoDiaLibre;
  readonly dias: number;
}

/**
 * Los dias libres que hay que pintar, en orden.
 *
 * [H58] Demora y detencion son conceptos distintos con costos distintos: no se
 * suman ni se resumen en un numero. [H54] `minimo` es la cota baja de un rango
 * negociable ("14-21days") y por eso viaja aparte, no como si fuera un valor
 * cerrado.
 *
 * Lista vacia cuando la tarifa no declara ninguno. La interfaz lo dice con
 * palabras; no pinta un cero.
 */
export function resumirDiasLibres(d: DiasLibres): DiaLibre[] {
  const salida: DiaLibre[] = [];
  if (d.demora !== null) salida.push({ tipo: 'demora', dias: d.demora });
  if (d.detencion !== null) salida.push({ tipo: 'detencion', dias: d.detencion });
  if (d.minimo !== null) salida.push({ tipo: 'minimo', dias: d.minimo });
  return salida;
}

// ---------------------------------------------------------------------------
// Carrier
// ---------------------------------------------------------------------------

export type EstadoCarrier =
  | 'IDENTIFICADO'
  | 'CODIGO_PENDIENTE'
  | 'ENMASCARADO'
  | 'NO_DECLARADO'
  /**
   * [H83] La celda nombra dos aerolineas: "DL/AA", "MU/UC". La tarifa vale
   * igual la opere una u otra y el proveedor no dice cual.
   *
   * Cae en la rama de abajo --se pinta el estado, en ambar-- y eso es lo
   * correcto: elegir una de las dos para enseñarla como "el carrier" seria
   * decidir por el proveedor y en silencio. El literal completo acompaña, que
   * es donde el agente ve las dos.
   */
  | 'MULTIPLE';

export interface CarrierApi {
  readonly nombre: string | null;
  readonly literal: string | null;
  readonly estado: EstadoCarrier;
}

export interface CarrierVista {
  /** Clave de i18n cuando el carrier no tiene nombre propio que mostrar. */
  readonly clave: string | null;
  /** El nombre del catalogo, ya normalizado. Traducible: no lo es. */
  readonly nombre: string | null;
  /** [CLAUDE.md, Idioma] `carrier_literal` tal como llego. Se pinta sin
   *  traducir ni limpiar, y acompaña siempre que exista. */
  readonly literal: string | null;
  /** El estado no es cosmetico: decide si el precio se puede atribuir a una
   *  naviera concreta. */
  readonly estado: EstadoCarrier;
}

/**
 * Como se presenta un carrier.
 *
 * [H44][H56] Un CODIGO_PENDIENTE es "CARRIER A": el proveedor lo codifico y no
 * dira cual es hasta que haya reserva. Pintarlo en blanco lo haria
 * indistinguible de una tarifa a la que se le olvido el dato, y son cosas
 * distintas: una esconde al carrier a proposito y la otra no lo sabe.
 *
 * Un IDENTIFICADO sin nombre en el catalogo cae al literal antes que a "no
 * declarado": el texto del proveedor es peor dato que el normalizado, pero es
 * mejor que ninguno.
 */
export function presentarCarrier(c: CarrierApi): CarrierVista {
  if (c.estado === 'IDENTIFICADO' && c.nombre !== null && c.nombre.trim() !== '') {
    return { clave: null, nombre: c.nombre, literal: c.literal, estado: c.estado };
  }
  if (c.estado === 'IDENTIFICADO' && c.literal !== null && c.literal.trim() !== '') {
    return { clave: null, nombre: null, literal: c.literal, estado: c.estado };
  }
  return {
    clave: `carrier.${c.estado}`,
    nombre: null,
    literal: c.literal,
    estado: c.estado,
  };
}
